<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Services" subTitle="Management" />

    <div class="card bg-white border-0 rounded-3 mb-4">
      <div class="card-body p-0">
        <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 p-4">
          <form class="position-relative table-src-form me-0" @submit.prevent>
            <input type="text" class="form-control" placeholder="Search services..." v-model="searchQuery" />
            <i class="material-symbols-outlined position-absolute top-50 start-0 translate-middle-y">search</i>
          </form>
          <RouterLink to="/services/create" v-if="isSuperAdmin" class="btn btn-outline-primary py-1 px-2 px-sm-4 fs-14 fw-medium rounded-3 hover-bg">
            <span class="py-sm-1 d-block">
              <i class="ri-add-line d-none d-sm-inline-block me-1"></i>
              <span>Add New Service</span>
            </span>
          </RouterLink>
        </div>

        <div class="default-table-area style-two default-table-width">
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">ID</th>
                  <th scope="col">Name</th>
                  <th scope="col">Category</th>
                  <th scope="col">Base Price</th>
                  <th scope="col">Akses / Mitra</th>
                  <th scope="col" v-if="isSuperAdmin">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="6" class="text-center py-4">Loading...</td>
                </tr>
                <tr v-else-if="filteredServices.length === 0">
                  <td colspan="6" class="text-center py-4">No services found.</td>
                </tr>
                <tr v-else v-for="service in filteredServices" :key="service.service_id">
                  <td>#{{ service.service_id }}</td>
                  <td>
                    <h6 class="mb-0 fs-14 fw-medium">{{ service.service_name }}</h6>
                    <span v-if="service.service_type" class="badge bg-secondary bg-opacity-10 text-secondary border border-secondary-subtle fs-11 mt-1">
                      {{ service.service_type }}
                    </span>
                  </td>
                  <td class="text-body">{{ service.category }}</td>
                  <td class="text-body">Rp {{ Number(service.base_price).toLocaleString('id-ID') }}</td>
                  <td>
                    <span v-if="service.is_campus_exclusive">
                      <span v-if="service.partners && service.partners.length > 0" class="badge bg-warning bg-opacity-20 text-warning-emphasis d-inline-flex align-items-center gap-1" :title="service.partners.map((p: any) => p.name).join(', ')">
                        <i class="material-symbols-outlined fs-14">school</i>
                        <span>{{ service.partners.length === 1 ? service.partners[0].name : service.partners.length + ' Kampus Mitra' }}</span>
                      </span>
                      <span v-else class="badge bg-primary bg-opacity-15 text-primary d-inline-flex align-items-center gap-1">
                        <i class="material-symbols-outlined fs-14">verified</i>
                        <span>Semua Kampus Mitra</span>
                      </span>
                    </span>
                    <span v-else class="badge bg-light text-muted">
                      Umum
                    </span>
                  </td>
                  <td v-if="isSuperAdmin">
                    <div class="d-flex align-items-center gap-1">
                      <RouterLink :to="`/services/${service.service_id}/edit`" class="ps-0 border-0 bg-transparent lh-1 position-relative top-2">
                        <i class="material-symbols-outlined fs-16 text-body">edit</i>
                      </RouterLink>
                      
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted } from 'vue';
import PageTitle from '@/components/Common/PageTitle.vue';
import serviceService from '@/services/serviceService';

export default defineComponent({
  name: 'ServiceListPage',
  components: {
    PageTitle
  },
  setup() {
    const services = ref<any[]>([]);
    const loading = ref(true);
    const searchQuery = ref('');

    const isSuperAdmin = computed(() => {
      const userStr = localStorage.getItem('user');
      if (!userStr) return false;
      try {
        const user = JSON.parse(userStr);
        return user.role === 'super_admin';
      } catch (e) {
        return false;
      }
    });

    const fetchServices = async () => {
      loading.value = true;
      try {
        const response = await serviceService.getAll();
        services.value = response.data;
      } catch (error) {
        console.error('Error fetching services:', error);
      } finally {
        loading.value = false;
      }
    };

    const deleteService = async (id: number) => {
      if (confirm('Are you sure you want to delete this service?')) {
        try {
          await serviceService.delete(id);
          fetchServices();
        } catch (error) {
          console.error('Error deleting service:', error);
        }
      }
    };

    const filteredServices = computed(() => {
      if (!searchQuery.value) return services.value;
      const q = searchQuery.value.toLowerCase();
      return services.value.filter(s => 
        s.service_name.toLowerCase().includes(q) || 
        s.category.toLowerCase().includes(q)
      );
    });

    onMounted(() => {
      fetchServices();
    });

    return {
      services,
      loading,
      searchQuery,
      filteredServices,
      deleteService,
      isSuperAdmin
    };
  }
});
</script>
